/**
 * CreditoVistaModal Component
 * 
 * Modal específico para criar/editar forma de recebimento do tipo Crédito à Vista.
 */

import { useState, useEffect } from 'react'
import Modal from '../../../../../components/ERP/Modal/Modal'
import FormField from '../../../../../components/ERP/Form/FormField'
import { CreditCard } from 'lucide-react'

const CARD_BRANDS = [
  { value: 'MASTER', label: 'Mastercard' },
  { value: 'VISA', label: 'Visa' },
  { value: 'ELO', label: 'Elo' },
  { value: 'AMEX', label: 'American Express' },
  { value: 'HIPERCARD', label: 'Hipercard' },
  { value: 'BANRISUL', label: 'Banrisul' }
]

function CreditoVistaModal({ isOpen, onClose, onSubmit, editingForma, accounts, errors }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    accountId: '',
    taxRate: '',
    cardBrand: '',
    active: true
  })

  useEffect(() => {
    if (editingForma) {
      setFormData({
        name: editingForma.name || '',
        description: editingForma.description || '',
        accountId: editingForma.account_id || '',
        taxRate: editingForma.tax_rate || '',
        cardBrand: editingForma.card_brand || '',
        active: editingForma.active !== undefined ? editingForma.active : true
      })
    } else {
      setFormData({
        name: '',
        description: '',
        accountId: '',
        taxRate: '',
        cardBrand: '',
        active: true
      })
    }
  }, [editingForma, isOpen])

  // Auto-gerar nome quando bandeira ou taxa mudarem
  useEffect(() => {
    if (formData.cardBrand && formData.taxRate) {
      const brandLabel = CARD_BRANDS.find(b => b.value === formData.cardBrand)?.label || formData.cardBrand
      setFormData(prev => ({
        ...prev,
        name: `Crédito à Vista ${brandLabel}${formData.taxRate ? ` (${parseFloat(formData.taxRate).toFixed(2)}%)` : ''}`
      }))
    } else if (formData.cardBrand) {
      const brandLabel = CARD_BRANDS.find(b => b.value === formData.cardBrand)?.label || formData.cardBrand
      setFormData(prev => ({
        ...prev,
        name: `Crédito à Vista ${brandLabel}`
      }))
    }
  }, [formData.cardBrand, formData.taxRate])

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      ...formData,
      provider: formData.cardBrand === 'BANRISUL' ? 'BANRISUL' : 'PAGSEGURO',
      paymentType: 'CRÉDITO_À_VISTA',
      taxRate: formData.taxRate ? parseFloat(formData.taxRate) : 0,
      installments: null,
      requiresConfirmation: false
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingForma ? 'Editar Crédito à Vista' : 'Nova Forma: Crédito à Vista'}
      icon={<CreditCard size={24} />}
      size="medium"
      footer={
        <>
          <button
            className="btn-secondary"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            className="btn-primary"
            onClick={handleSubmit}
          >
            {editingForma ? 'Salvar' : 'Criar'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <FormField
          label="Bandeira do Cartão"
          name="cardBrand"
          type="select"
          value={formData.cardBrand}
          onChange={(e) => setFormData({ ...formData, cardBrand: e.target.value })}
          error={errors.cardBrand}
          required
          options={[
            { value: '', label: 'Selecione a bandeira...' },
            ...CARD_BRANDS
          ]}
          helpText="Selecione a bandeira do cartão de crédito"
        />

        <FormField
          label="Taxa (%)"
          name="taxRate"
          type="number"
          value={formData.taxRate}
          onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
          error={errors.taxRate}
          required
          placeholder="0.00"
          step="0.01"
          min="0"
          max="100"
          helpText="Taxa percentual aplicada sobre o valor bruto (ex: 2.99 para 2,99%)"
        />

        <FormField
          label="Nome"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          error={errors.name}
          required
          placeholder="Será gerado automaticamente"
          helpText="Nome gerado automaticamente, mas pode ser editado"
        />

        <FormField
          label="Descrição"
          name="description"
          type="textarea"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          error={errors.description}
          placeholder="Descrição opcional"
        />

        <FormField
          label="Conta Financeira"
          name="accountId"
          type="select"
          value={formData.accountId}
          onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
          error={errors.accountId}
          options={[
            { value: '', label: 'Selecione uma conta...' },
            ...accounts.map(account => ({
              value: account.id.toString(),
              label: `${account.code} - ${account.name}`
            }))
          ]}
        />

        <FormField
          label="Status"
          name="active"
          type="select"
          value={formData.active ? 'true' : 'false'}
          onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
          error={errors.active}
          options={[
            { value: 'true', label: 'Ativa' },
            { value: 'false', label: 'Inativa' }
          ]}
        />

        {errors.submit && (
          <div className="form-error-message">{errors.submit}</div>
        )}
      </form>
    </Modal>
  )
}

export default CreditoVistaModal
